import { useStripe, useElements } from "@stripe/react-stripe-js";
import { SetupIntent } from "@stripe/stripe-js";
import { createContext, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSetDefaultPaymentMethodMutation } from "src/api/services/paymentMethodsApi";
import {
  useCreateSetupIntentMutation,
  useOneTimePaymentMutation,
} from "src/api/services/paymentsApi";
import { Coupon, ProductPrice, StripeInvoice } from "src/api/types";
import { usePaymentSearchParams } from "src/hooks/usePaymentSearchParams";

type ContextType = {
  payWithPaymentIntent: () => Promise<StripeInvoice | undefined>;
  payWithSetupIntent: () => Promise<SetupIntent | undefined>;
};

export const PaymentContextProvider = createContext<ContextType>({
  payWithPaymentIntent: async () => undefined,
  payWithSetupIntent: async () => undefined,
});

interface Props {
  product: ProductPrice;
  children: React.ReactNode;
  coupon: Coupon | null;
}

const PaymentProvider = ({ children, product, coupon }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processOneTimePayment] = useOneTimePaymentMutation();
  const [createSetUpIntent] = useCreateSetupIntentMutation();
  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();
  const { pathname } = useLocation();
  const {
    dosage,
    priceId,
    variant,
    form,
    quantity,
    couponName,
    chosenPriceId,
  } = usePaymentSearchParams();

  const returnUrl = useCallback(
    (options?: { invoiceId?: string }) => {
      const params = new URLSearchParams({
        price: product.stripe_id,
        ...(chosenPriceId != null && { id: chosenPriceId }),
        ...(couponName != null && { coupon: couponName }),
        ...(variant != null && { variant }),
        ...(priceId != null && { price: priceId }),
        ...(quantity != null && { quantity: quantity }),
        ...(form != null && { form }),
        ...(dosage != null && { dosage }),
        ...(options?.invoiceId != null && {
          checkout_session: options.invoiceId,
        }),
      });

      return `${window.origin}${pathname}${
        params.toString() ? `?${params}` : ""
      }`;
    },
    [
      chosenPriceId,
      couponName,
      dosage,
      form,
      pathname,
      priceId,
      product.stripe_id,
      quantity,
      variant,
    ],
  );

  const payWithPaymentIntent = useCallback(async () => {
    if (!stripe || !elements) {
      throw new Error("Still loading. Try again in a moment.");
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(submitError.message ?? "Something went wrong");
    }

    const { data } = await processOneTimePayment({
      priceId: product.stripe_id,
      couponId: coupon?.id,
      quantity: quantity != null ? Number(quantity) : undefined,
    });

    const clientSecret = data?.paymentIntent?.client_secret;
    if (!clientSecret) {
      throw new Error("No client_secret provided");
    }

    const url = returnUrl({ invoiceId: data.invoice.id });

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            address: {
              country: "US",
            },
          },
          allow_redisplay: "always",
        },
        return_url: url,
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    if (error) {
      throw error;
    }

    return data.invoice;
  }, [
    coupon?.id,
    elements,
    processOneTimePayment,
    product.stripe_id,
    quantity,
    returnUrl,
    stripe,
  ]);

  const payWithSetupIntent = useCallback(async () => {
    if (!stripe || !elements) {
      throw new Error("Still loading. Try again in a moment.");
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      throw new Error(submitError.message ?? "Something went wrong");
    }

    const { data } = await createSetUpIntent();

    const clientSecret = data?.clientSecret;
    if (!clientSecret) {
      throw new Error("No client_secret provided");
    }

    const url = returnUrl();

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            address: {
              country: "US",
            },
          },
          allow_redisplay: "always",
        },
        return_url: url,
      },
      // Uncomment below if you only want redirect for redirect-based payments
      redirect: "if_required",
    });

    if (setupIntent != null) {
      await setDefaultPaymentMethod({
        paymentMethodId: setupIntent.payment_method as string,
      }).unwrap();
    }

    if (error) {
      throw error;
    }

    return setupIntent;
  }, [createSetUpIntent, elements, returnUrl, setDefaultPaymentMethod, stripe]);

  const values = useMemo(
    () => ({
      payWithPaymentIntent,
      payWithSetupIntent,
    }),
    [payWithPaymentIntent, payWithSetupIntent],
  );

  return (
    <PaymentContextProvider.Provider value={values}>
      {children}
    </PaymentContextProvider.Provider>
  );
};

export default PaymentProvider;
